import { Button } from "@gigpro/ui/Button";
import { Checkbox } from "@gigpro/ui/Checkbox";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { useNow } from "@gigpro/ui/utils/useNow";
import { formatCurrency } from "@gigpro/utils";
import { getHireJobsProMatchStatusListQueryKey, type ProMatchStatus } from "@sidegigllc/api/client";
import { useQueryClient } from "@tanstack/react-query";
import { intlFormatDistance } from "date-fns";
import { useJobsGatedAction } from "domains/hire/containers/JobsGateStep";
import { NewJobDialog } from "domains/hire/containers/NewJobDialog";
import { jobsPlanHireFeeMessage, pricingForJobsStatus } from "domains/hire/hireFeePlan";
import { useJobsSubscription } from "domains/hire/hooks/useJobsSubscription";
import { canScheduleWorkingInterview } from "domains/hire/utils";
import React from "react";
import { useActiveLocationId } from "services/locationOrgContext";

type JobWithOfferStatus = ProMatchStatus & { offer_status?: string | null };

interface ProContext {
  name: string;
  imageId: string | null;
}

type DialogVariant = "invite" | "hire" | "interview";

interface InviteToJobDialogProps {
  open: boolean;
  onClose: () => void;
  jobs: Array<JobWithOfferStatus>;
  isLoading?: boolean;
  isSubmitting?: boolean;
  onConfirm: (jobIds: Array<string>) => void;
  proContext?: ProContext;
  variant?: DialogVariant;
  /** Hire variant only: hire the Pro without a job. Without it the option is not offered. */
  onHireDirectly?: () => void;
  /** Hire variant only: the Pro is still loading, so hiring directly can't start yet. */
  proLoading?: boolean;
  /** Hire variant only: the Pro failed to load, so hiring directly is off with an explanation. */
  proFailed?: boolean;
}

const variantCopy: Record<DialogVariant, { title: string; description: string; confirm: string }> = {
  invite: {
    title: "Invite to job",
    description: "Create or share an existing job with this pro and invite them to apply",
    confirm: "Confirm",
  },
  hire: {
    title: "Send hire offer",
    description: "Select a job posting to send a hire offer to this pro",
    confirm: "Continue",
  },
  interview: {
    title: "Schedule interview",
    description: "Select a job to interview this pro for",
    confirm: "Continue",
  },
};

export const InviteToJobDialog: React.FC<InviteToJobDialogProps> = ({ open, onClose, ...bodyProps }) => {
  // The id is known on the first render, so the gate applies from the start (the location row itself loads later)
  const [locationId] = useActiveLocationId();
  const queryClient = useQueryClient();
  const [newJobOpen, setNewJobOpen] = React.useState(false);

  return (
    <>
      <Dialog open={open} onClose={onClose} className="max-w-md" bottomSheet closeOnBackdropClick>
        {locationId ? (
          <GatedInviteToJobDialogBody
            locationId={locationId}
            onClose={onClose}
            onPostNewJob={() => setNewJobOpen(true)}
            {...bodyProps}
          />
        ) : (
          <InviteToJobDialogBody
            gate={UNGATED}
            onClose={onClose}
            onPostNewJob={() => setNewJobOpen(true)}
            {...bodyProps}
          />
        )}
      </Dialog>
      {locationId && (
        <NewJobDialog
          open={newJobOpen}
          onClose={() => {
            setNewJobOpen(false);
            queryClient.invalidateQueries({ queryKey: getHireJobsProMatchStatusListQueryKey() });
          }}
          locationId={locationId}
        />
      )}
    </>
  );
};

type BodyProps = Omit<InviteToJobDialogProps, "open"> & {
  onPostNewJob: () => void;
};

type JobsGate = ReturnType<typeof useJobsGatedAction>;

/** Without an active location there is no plan to check. */
const UNGATED: JobsGate = { run: (action) => action(), step: null, ready: true };

const GatedInviteToJobDialogBody: React.FC<BodyProps & { locationId: number }> = ({ locationId, ...bodyProps }) => (
  <InviteToJobDialogBody gate={useJobsGatedAction(locationId)} {...bodyProps} />
);

const InviteToJobDialogBody: React.FC<BodyProps & { gate: JobsGate }> = ({
  gate,
  onClose,
  jobs,
  isLoading,
  isSubmitting,
  onConfirm,
  proContext,
  variant = "invite",
  onPostNewJob,
  onHireDirectly,
  proLoading,
  proFailed,
}) => {
  const copy = variantCopy[variant];
  const jobsSubscription = useJobsSubscription();
  const jobsPlanMessage =
    variant === "hire" &&
    jobsPlanHireFeeMessage(pricingForJobsStatus(jobsSubscription), proContext?.name, jobsSubscription);
  const directHireOffered = variant === "hire" && !!onHireDirectly;
  const [pickedJobIds, setPickedJobIds] = React.useState<Array<string>>([]);

  // Jobs that become unavailable drop out of the selection
  const selectedJobIds = pickedJobIds.filter((id) => !jobs.some((j) => j.id === id && !isJobAvailable(j, variant)));

  const handleConfirm = () => {
    if (selectedJobIds.length > 0) {
      void gate.run(() => onConfirm(selectedJobIds));
    }
  };

  const handleToggleJob = (job: JobWithOfferStatus) => {
    if (!isJobAvailable(job, variant)) return;
    if (variant !== "invite") {
      // Single-select for hire + interview flows
      setPickedJobIds(selectedJobIds.includes(job.id) ? [] : [job.id]);
    } else {
      setPickedJobIds(
        selectedJobIds.includes(job.id) ? selectedJobIds.filter((id) => id !== job.id) : [...selectedJobIds, job.id],
      );
    }
  };

  const canSubmit = selectedJobIds.length > 0 && gate.ready;

  // A location without the Jobs plan sees the trial before anything reaches a person
  if (gate.step) return gate.step;

  return (
    <>
      <DialogTitle className="pb-0 [&>h2]:text-extraLarge">
        {copy.title}
        {proContext && ` · ${proContext.name}`}
      </DialogTitle>

      <p className="text-normal text-gray">{copy.description}</p>

      {jobsPlanMessage && <p className="text-normal text-black">{jobsPlanMessage}</p>}

      {directHireOffered && (
        <Button
          variant={jobs.length === 0 && !isLoading ? "contained" : "outlined"}
          className="w-full"
          disabled={proLoading || proFailed}
          onClick={onHireDirectly}
        >
          {`Hire ${proContext?.name ?? "this pro"} directly`}
        </Button>
      )}
      {directHireOffered && proFailed && (
        <p role="alert" className="text-small text-error">
          We couldn't load this Pro, so you can't hire them directly right now. Close this and try again.
        </p>
      )}

      <Button variant="outlined" className="mb-1 w-full" onClick={onPostNewJob}>
        Post a new job
      </Button>

      <p className="text-normal font-semibold">Select from your posted jobs</p>

      <JobList
        jobs={jobs}
        isLoading={isLoading}
        selectedJobIds={selectedJobIds}
        onToggleJob={handleToggleJob}
        variant={variant}
      />

      <DialogFooter className="grid grid-cols-2 gap-2">
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button loading={isSubmitting} variant="contained" onClick={handleConfirm} disabled={!canSubmit}>
          {copy.confirm}
        </Button>
      </DialogFooter>
    </>
  );
};

interface JobListProps {
  jobs: Array<JobWithOfferStatus>;
  isLoading?: boolean;
  selectedJobIds: Array<string>;
  onToggleJob: (job: JobWithOfferStatus) => void;
  variant: DialogVariant;
}

const JobList: React.FC<JobListProps> = ({ jobs, isLoading, selectedJobIds, onToggleJob, variant }) => {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-8 animate-pulse rounded-sm bg-light-gray" />
        ))}
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="py-6 text-center">
        <p className="text-small text-gray">No jobs available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {jobs.map((job) => (
        <JobRow
          key={job.id}
          job={job}
          isSelected={selectedJobIds.includes(job.id)}
          onToggle={() => onToggleJob(job)}
          variant={variant}
        />
      ))}
    </div>
  );
};

interface JobRowProps {
  job: JobWithOfferStatus;
  isSelected: boolean;
  onToggle: () => void;
  variant: DialogVariant;
}

const JobRow: React.FC<JobRowProps> = ({ job, isSelected, onToggle, variant }) => {
  const isDisabled = !isJobAvailable(job, variant);
  const now = useNow();
  const statusText = getJobStatusText(job, now);

  return (
    <button
      type="button"
      id={job.id}
      data-testid={`job_row_${job.id}`}
      onClick={onToggle}
      disabled={isDisabled}
      className={`flex w-full items-center justify-between rounded-sm bg-background px-3 py-2 ${
        isDisabled ? "cursor-not-allowed" : "cursor-pointer hover:bg-light-gray"
      }`}
    >
      <div className="flex items-center gap-2">
        <Checkbox checked={isSelected} disabled={isDisabled} asDiv />
        <span className={`text-normal font-semibold ${isDisabled ? "text-medium-gray" : "text-black"}`}>
          {job.name}
        </span>
        <span className={`text-normal ${isDisabled ? "text-medium-gray" : "text-black"}`}>
          {formatCurrency(Number(job.wage))}/hr
        </span>
      </div>
      {statusText && <span className="text-normal text-medium-gray">{statusText}</span>}
    </button>
  );
};

const ACTIVE_OFFER_STATUSES = new Set(["SENT"]);

const isJobAvailable = (job: JobWithOfferStatus, variant: DialogVariant): boolean => {
  // Hire variant: only block jobs with active (SENT) offers or ineligibility — allow re-offering after DECLINED/EXPIRED/CANCELLED
  if (variant === "hire")
    return !(job.offer_status && ACTIVE_OFFER_STATUSES.has(job.offer_status)) && !job.ineligibility_reason;
  // Interview variant: eligible open job the pro isn't already scheduled/offered for
  if (variant === "interview") return !job.ineligibility_reason && canScheduleWorkingInterview(job.applied_stage);
  // Invite variant: block if already invited, applied, has offer, or ineligible
  return !job.matched_ts && !job.applied_at && !job.ineligibility_reason && !job.offer_status;
};

// Precedence: offer > applied > invited > ineligible
const getJobStatusText = (job: JobWithOfferStatus, now: Date): string | null => {
  if (job.offer_status) return getOfferStatusLabel(job.offer_status);
  if (job.applied_at) return `Applied ${intlFormatDistance(job.applied_at, now)}`;
  if (job.matched_ts) return `Invited ${intlFormatDistance(job.matched_ts, now)}`;
  if (job.ineligibility_reason) return job.ineligibility_reason;
  return null;
};

const getOfferStatusLabel = (status: string): string => {
  switch (status) {
    case "SENT":
      return "Offer pending";
    case "ACCEPTED":
      return "Offer accepted";
    case "DECLINED":
      return "Offer declined";
    case "EXPIRED":
      return "Offer expired";
    case "CANCELLED":
      return "Offer cancelled";
    default:
      return `Offer: ${status}`;
  }
};
